import { useEffect, useMemo, type FormEvent, type KeyboardEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useLang } from '../i18n'
import { api, type AskResult, type KbSource } from '../lib/api'
import { useKnowledgeStore } from '../lib/knowledgeStore'

/** A source aggregated from the retrieval result — one row per distinct document. */
interface MatchedSource {
  sourceId: string
  sourceName: string
  chunks: string[]
}

/** Map a filename to the mockup's coloured file-type badge. */
function ftypeOf(name: string): { label: string; cls: string } {
  const ext = name.split('.').pop()?.toLowerCase() ?? ''
  if (ext === 'pdf') return { label: 'PDF', cls: 'pdf' }
  if (ext === 'doc' || ext === 'docx') return { label: 'DOC', cls: 'docx' }
  if (ext === 'ppt' || ext === 'pptx') return { label: 'PPT', cls: 'pptx' }
  if (ext === 'md' || ext === 'markdown') return { label: 'MD', cls: 'md' }
  if (ext === 'txt') return { label: 'TXT', cls: 'md' }
  return { label: 'DOC', cls: 'md' }
}

/** Format an ISO upload timestamp to a short local date; `—` when the backend has none. */
export function formatUploaded(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10)
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

/** Group retrieval chunks + citations into one row per distinct source. */
export function groupSources(data: AskResult | undefined): MatchedSource[] {
  if (!data) return []
  const byId = new Map<string, MatchedSource>()
  for (const c of data.chunks) {
    const existing = byId.get(c.sourceId)
    if (existing) existing.chunks.push(c.content)
    else byId.set(c.sourceId, { sourceId: c.sourceId, sourceName: c.sourceName, chunks: [c.content] })
  }
  // Include cited sources that returned no chunk snippet (defensive — keeps the list complete).
  for (const cit of data.citations) {
    if (!byId.has(cit.sourceId)) byId.set(cit.sourceId, { sourceId: cit.sourceId, sourceName: cit.sourceName, chunks: [] })
  }
  return [...byId.values()]
}

export function KnowledgeView({ bid }: { bid: string | null }) {
  const { t } = useLang()
  const query = useKnowledgeStore((s) => s.query)
  const submittedQuery = useKnowledgeStore((s) => s.submittedQuery)
  const selectedSourceId = useKnowledgeStore((s) => s.selectedSourceId)
  const setQuery = useKnowledgeStore((s) => s.setQuery)
  const submit = useKnowledgeStore((s) => s.submit)
  const select = useKnowledgeStore((s) => s.select)
  const reset = useKnowledgeStore((s) => s.reset)

  // Reset the search/selection when switching bids so state never leaks across bids.
  useEffect(() => {
    reset()
  }, [bid, reset])

  // PRIMARY content: the bid's indexed sources (ACL-trimmed server-side). This is a plain listing,
  // not a search — it is always shown for an open bid (populated via the SharePoint attach flow
  // or the console agent's index_document tool).
  const sourcesQ = useQuery({
    queryKey: ['sources', bid],
    queryFn: () => api.listSources(bid as string),
    enabled: Boolean(bid),
  })
  const indexed: KbSource[] = sourcesQ.data?.sources ?? []
  const totalChunks = useMemo(() => indexed.reduce((n, s) => n + s.chunks, 0), [indexed])

  // Secondary/companion: server-side retrieval over the bid's KB (ACL-trimmed). Not the AI "ask"
  // (no generation) — it surfaces which indexed sources match a term. Enabled only once submitted.
  const results = useQuery({
    queryKey: ['knowledge', bid, submittedQuery],
    queryFn: () => api.ask(bid as string, submittedQuery),
    enabled: Boolean(bid) && submittedQuery.length > 0,
  })
  const matches = useMemo(() => groupSources(results.data), [results.data])

  const onSearch = (e: FormEvent): void => {
    e.preventDefault()
    submit()
  }
  const onRowKey = (e: KeyboardEvent, id: string): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      select(id)
    }
  }

  return (
    <div className="kbwrap">
      <div className="kbhead">
        <div className="ttl">
          <h1>{t('kb_title')}</h1>
          <p>{t('kb_sub')}</p>
        </div>
        <div className="acts">
          <form className="search" role="search" onSubmit={onSearch}>
            <span aria-hidden="true">🔎</span>
            <input
              type="search"
              aria-label={t('kb_search')}
              placeholder={t('kb_search')}
              value={query}
              disabled={!bid}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button type="submit" className="visually-hidden">{t('kb_search_go')}</button>
          </form>
        </div>
      </div>

      {!bid ? (
        <p className="kb-empty">{t('kb_no_bid')}</p>
      ) : (
        <>
          {/* ── Indexed sources (PRIMARY: the bid's whole KB, listed) ──────── */}
          <section className="srcgroup" aria-labelledby="kb-sources-h">
            <div className="srchead">
              <div className="si up" aria-hidden="true">◈</div>
              <div>
                <div className="nm" id="kb-sources-h">{t('kb_sources_head')}</div>
                <div className="sub">{t('kb_sources_sub')}</div>
              </div>
              {indexed.length > 0 && (
                <div className="right">
                  <span>{indexed.length} {t('s_docs').toLowerCase()} · {totalChunks} {t('kb_chunks_word')}</span>
                </div>
              )}
            </div>

            {sourcesQ.isLoading ? (
              <p className="kb-empty">{t('loading')}</p>
            ) : sourcesQ.isError ? (
              <p className="kb-empty">{t('kb_sources_error')}</p>
            ) : indexed.length === 0 ? (
              <p className="kb-empty">{t('kb_sources_empty')}</p>
            ) : (
              <table className="kbtable">
                <thead>
                  <tr>
                    <th>{t('t_doc')}</th>
                    <th>{t('t_cat')}</th>
                    <th>{t('t_chunks')}</th>
                    <th>{t('t_uploaded')}</th>
                  </tr>
                </thead>
                <tbody>
                  {indexed.map((s) => {
                    const ft = ftypeOf(s.name)
                    return (
                      <tr
                        key={s.id}
                        className={selectedSourceId === s.id ? 'sel' : ''}
                        tabIndex={0}
                        aria-selected={selectedSourceId === s.id}
                        onClick={() => select(s.id)}
                        onKeyDown={(e) => onRowKey(e, s.id)}
                      >
                        <td>
                          <div className="doc">
                            <span className={'ftype ' + ft.cls} aria-hidden="true">{ft.label}</span>
                            <span className="dn"><b>{s.name}</b></span>
                          </div>
                        </td>
                        <td>
                          {s.category
                            ? <span className="badge cat">{s.category}</span>
                            : <span className="kb-muted">{t('kb_uncategorised')}</span>}
                        </td>
                        <td className="chunks">{s.chunks}</td>
                        <td className="kb-muted">{formatUploaded(s.uploadedAt)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </section>

          {/* ── Search over indexed content (secondary — retrieval, NOT AI) ── */}
          <section className="srcgroup" aria-labelledby="kb-results-h">
            <div className="srchead">
              <div className="si up" aria-hidden="true">🔎</div>
              <div>
                <div className="nm" id="kb-results-h">{t('kb_results_head')}</div>
                <div className="sub">{t('kb_results_sub')}</div>
              </div>
              {matches.length > 0 && (
                <div className="right">
                  <span>{matches.length} {t('s_docs').toLowerCase()} · {results.data?.chunks.length ?? 0} {t('kb_matched')}</span>
                </div>
              )}
            </div>

            {submittedQuery.length === 0 ? (
              <p className="kb-empty">{t('kb_search_prompt')}</p>
            ) : results.isLoading ? (
              <p className="kb-empty">{t('kb_searching')}</p>
            ) : matches.length === 0 ? (
              <p className="kb-empty">{t('kb_no_results')}</p>
            ) : (
              <table className="kbtable">
                <thead>
                  <tr>
                    <th>{t('t_doc')}</th>
                    <th>{t('t_chunks')}</th>
                    <th>{t('kb_preview')}</th>
                  </tr>
                </thead>
                <tbody>
                  {matches.map((s) => {
                    const ft = ftypeOf(s.sourceName)
                    return (
                      <tr
                        key={s.sourceId}
                        className={selectedSourceId === s.sourceId ? 'sel' : ''}
                        tabIndex={0}
                        aria-selected={selectedSourceId === s.sourceId}
                        onClick={() => select(s.sourceId)}
                        onKeyDown={(e) => onRowKey(e, s.sourceId)}
                      >
                        <td>
                          <div className="doc">
                            <span className={'ftype ' + ft.cls} aria-hidden="true">{ft.label}</span>
                            <span className="dn"><b>{s.sourceName}</b></span>
                          </div>
                        </td>
                        <td className="chunks">{s.chunks.length}</td>
                        <td className="kb-snippet">{s.chunks[0]?.slice(0, 120) ?? '—'}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </div>
  )
}
